import React from 'react';

import { PieChartOutlined } from '@ant-design/icons';
import { formatNumberWithDigits } from '@utils/common';
import { Skeleton } from 'antd';
import ReactECharts from 'echarts-for-react';

interface DashboardDonutChartProps {
  title: string;
  value: number | string | undefined;
  quantifier?: string;
  option?: any;
}

function DashboardDonutChart({
  title,
  value,
  quantifier = '%',
  option = {},
}: DashboardDonutChartProps) {
  if (value === undefined) {
    return (
      <div
        style={{
          height: '170px',
          width: '170px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Skeleton.Node active style={{ borderRadius: '50%' }}>
          <PieChartOutlined style={{ fontSize: 68, color: '#bfbfbf' }} />
        </Skeleton.Node>
      </div>
    );
  }

  const isValueNaN = Number.isNaN(value) || (!value && value !== 0);
  let formattedValue = '0';
  if (isValueNaN) {
    formattedValue = 'N/A';
  } else {
    formattedValue = formatNumberWithDigits(Number(value), 1);
  }

  const centralTextThreshold = 5;

  function organizeCentralTextHandler() {
    if (isValueNaN) {
      return 'N/A';
    }
    if (value === 100) {
      return `100${quantifier}`;
    }
    return `${formattedValue}${quantifier}`;
  }

  const baseOption = {
    tooltip: {
      trigger: 'item',
      formatter: `${Number(value) !== 100 ? formattedValue : 100}${
        !isValueNaN ? quantifier : ''
      }`,
    },
    series: [
      {
        name: title,
        type: 'pie',
        radius: ['60%', '80%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: 'center',
        },
        labelLine: {
          show: false,
        },
        data: [
          {
            value:
              quantifier !== '%' && !Number.isNaN(value) ? 100 : Number(value),
            itemStyle: { color: '#2F3E73' },
          },
          {
            value:
              quantifier !== '%' || Number.isNaN(value)
                ? 0
                : 100 - Number(value),
            itemStyle: { color: '#E4E7EC' },
          },
        ],
      },
    ],
    graphic: {
      type: 'text',
      left: 'center',
      top: 'center',
      style: {
        text: organizeCentralTextHandler(),
        fontSize: `${
          formattedValue.length > centralTextThreshold
            ? `${
                24 -
                (formattedValue.length -
                  centralTextThreshold -
                  Math.floor((formattedValue.length - 3) / 3)) *
                  2
              }px`
            : '28px'
        }`,
        fontWeight: 'bold',
        fill: '#2B3F60',
        cursor: 'default',
      },
    },
  };

  return (
    <ReactECharts
      option={{ ...baseOption, ...option }}
      style={{ height: '170px', width: '170px' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

export default DashboardDonutChart;
